<script lang="ts">
  export * from './DataViewFilters.keys';
  export * from './DataViewFilters.types';
  export * from './useFilters';
</script>

<script setup lang="ts">
  import { computed, inject, provide, ref, watch } from 'vue';

  import useMediaQuery from '../../composables/useMediaQuery/useMediaQuery';
  import { SCREEN_SIZES } from '../../constants';
  import { t } from '../../locale';
  import Box from '../Box/Box.vue';
  import Button from '../Button/Button.vue';
  import { DATA_VIEW_INJECTION } from '../DataView/DataView.vue';
  import FilterChip from '../FilterChip/FilterChip.vue';
  import Icon from '../Icon/Icon.vue';
  import Label from '../Label/Label.vue';
  import Modal, { type ModalProps } from '../Modal/Modal.vue';
  import SearchBar, { SearchBarProps } from '../SearchBar/SearchBar.vue';
  import { DATA_VIEW_FILTERS_UTILS_INJECTION } from './DataViewFilters.keys';
  import type { DrawerStyles, OnApplyFilters } from './DataViewFilters.types';
  import type { UseFiltersReturnType } from './useFilters';

  export interface DataViewFiltersProps {
    filtersLabelText?: string;
    /**
     * Props to pass to the `SearchBar` component.
     */
    searchBarProps?: SearchBarProps;
    showSearch?: boolean;
    /** 'cascade' displays all fields within every filter group; 'nested' displays only the group names and requires clicking a group to view its fields. */
    drawerStyle?: DrawerStyles;
    drawerProps?: ModalProps;
    /**
     * @deprecated The `activeGroup` prop is a sufficient replacement for this prop. A falsy `activeGroup` will hide the button and a truthy `activeGroup` will show it (when the `drawerStyle` is 'nested').
     *
     * **Note:** This prop has no effect when using a "cascade" `drawerStyle`.
     */
    showDrawerPreviousButton?: boolean;
    /**
     * Required when using filters. This prop should contain the return value of the `useFilters()` composable.
     */
    useFiltersInstance?: UseFiltersReturnType;
    onApply?: OnApplyFilters;
    /**
     * The name of the active filter group. The active filter group is determined by which instance of FilterDropdown or FilterDrawerItem is open.
     *
     * **Note:** This prop is required when using a "nested" `drawerStyle`, but has no effect when using a "cascade" `drawerStyle`.
     */
    activeGroup?: string;
  }

  export interface DataViewFiltersSlots {
    default?: () => unknown;
    drawer?: () => unknown;
    'filters-label'?: () => unknown;
  }

  const props = withDefaults(defineProps<DataViewFiltersProps>(), {
    filtersLabelText: t('ll.filterBy'),
    isLoading: false,
    drawerStyle: 'cascade',
    drawerProps: undefined,
    searchBarProps: undefined,
    showDrawerPreviousButton: false,
    showSearch: true,
    useFiltersInstance: undefined,
    onApply: undefined,
    activeGroup: undefined,
  });

  const emit = defineEmits<{
    /** When the drawer is opened */
    (e: 'open-drawer'): void;
    /** When the drawer is dismissed */
    (e: 'dismiss'): void;
    /** When the "Previous" button in the header is clicked */
    (e: 'previous'): void;
    /** When the "Reset" button is clicked while viewing a filter group */
    (e: 'reset-group'): void;
    /** When one of the "Reset All" buttons is clicked */
    (e: 'reset-all'): void;
  }>();

  const slots = defineSlots<DataViewFiltersSlots>();

  const isDesktop = useMediaQuery(`(min-width: ${SCREEN_SIZES.lg})`);
  const isViewingGroupsMenu = computed(() => props.drawerStyle === 'nested' && !props.activeGroup);
  const isViewingSingleGroup = computed(() => props.drawerStyle === 'nested' && props.activeGroup);

  const {
    density,
    isLoading: isDataViewLoading,
    isWithinModule,
    currentSearch,
    updateCurrentSearch,
  } = inject(DATA_VIEW_INJECTION.key, DATA_VIEW_INJECTION.defaults);

  provide(DATA_VIEW_FILTERS_UTILS_INJECTION.key, {
    useFiltersInstance: props.useFiltersInstance,
    drawerStyle: props.drawerStyle,
  });

  const totalActiveFiltersCount = computed(() => props.useFiltersInstance?.totalActiveFiltersCount.value ?? 0);
  /** The number of active filters in the currently active group. This is only used when the drawerStyle is 'nested'. */
  const activeGroupActiveFiltersCount = computed(
    () => Number(props.activeGroup && props.useFiltersInstance?.activeFiltersCounts.value[props.activeGroup]) || 0,
  );
  const isDrawerOpen = ref(false);

  async function handleApplyClick() {
    const { preventDismiss } = (await props.onApply?.()) || props.useFiltersInstance?.applyFilters() || {};

    if (!preventDismiss) {
      isDrawerOpen.value = false;
    }
  }

  function handleResetGroupClick() {
    if (!props.activeGroup) {
      return;
    }

    props.useFiltersInstance?.resetFilterGroup(props.activeGroup);
    emit('reset-group');
    isDrawerOpen.value = false;
  }

  function handleResetAllClick() {
    props.useFiltersInstance?.resetAllFilters();
    emit('reset-all');
    isDrawerOpen.value = false;
  }

  function onDismiss() {
    props.useFiltersInstance?.undoWorkingFilters();
    isDrawerOpen.value = false;
    emit('dismiss');
  }

  watch(isDrawerOpen, () => {
    if (isDrawerOpen.value) {
      emit('open-drawer');
    }
  });
</script>

<template>
  <Box
    class="stash-data-view-filters grid grid-cols-12 gap-6 p-3"
    :class="{ 'lg:p-6': density === 'comfortable', 'mb-3': !isWithinModule }"
    :radius="isWithinModule ? 'none' : 'rounded'"
    data-test="stash-data-view-filters"
    disable-padding
  >
    <SearchBar
      v-if="props.showSearch"
      class="col-span-12 md:col-span-6 lg:col-span-4"
      data-test="stash-data-view-filters|search-bar"
      :is-loading="isDataViewLoading"
      :label="t('ll.search')"
      :model-value="currentSearch"
      v-bind="props.searchBarProps"
      @search="(searchTerm) => updateCurrentSearch(searchTerm, { shouldEmit: true })"
    />
    <div
      v-if="slots.default"
      class="col-span-12 row-start-2 md:col-start-7 md:row-start-1 lg:col-span-8 lg:col-start-5"
    >
      <div class="hidden md:block">
        <slot name="filters-label">
          <Label>{{ props.filtersLabelText }}</Label>
        </slot>
      </div>
      <div class="flex gap-4">
        <FilterChip
          secondary
          class="!md:inline-flex !flex w-full justify-center gap-4 md:w-auto"
          data-test="stash-data-view-filters|drawer-toggle-button"
          :filter-count="totalActiveFiltersCount"
          @click="isDrawerOpen = true"
        >
          <span class="inline-flex items-center gap-3">
            <Icon name="filter-line" class="text-ice-700" />
            <span>{{ t('ll.filters') }}</span>
          </span>
        </FilterChip>
        <slot v-if="isDesktop"></slot>
        <Button v-if="totalActiveFiltersCount > 0 && isDesktop" inline @click="handleResetAllClick">
          {{ t('ll.resetAll') }}
        </Button>
      </div>
    </div>
    <Modal
      v-if="slots.drawer"
      data-test="stash-data-view-filters|drawer"
      disable-body-padding
      position="right"
      size="narrow"
      :is-open="isDrawerOpen"
      :title="t('ll.allFilters')"
      v-bind="props.drawerProps"
      @dismiss="onDismiss"
    >
      <template #headerAction>
        <Button
          v-if="isViewingSingleGroup"
          icon
          class="text-ice-100"
          data-test="stash-data-view-filters|drawer-previous-button"
          :aria-label="t('ll.previous')"
          :title="t('ll.previous')"
          @click="emit('previous')"
        >
          <Icon name="chevron-left" />
        </Button>
      </template>

      <slot name="drawer"></slot>

      <template #footer>
        <div class="flex justify-end gap-gutter">
          <Button v-if="totalActiveFiltersCount === 0" secondary @click="onDismiss">
            {{ t('ll.cancel') }}
          </Button>
          <Button
            v-if="(isViewingGroupsMenu || props.drawerStyle === 'cascade') && totalActiveFiltersCount > 0"
            secondary
            :disabled="isDataViewLoading"
            @click="handleResetAllClick"
          >
            {{ t('ll.resetAll') }}
          </Button>
          <Button
            v-if="isViewingSingleGroup && activeGroupActiveFiltersCount > 0"
            secondary
            :disabled="isDataViewLoading"
            @click="handleResetGroupClick"
          >
            {{ t('ll.reset') }}
          </Button>
          <Button
            v-if="isViewingSingleGroup || props.drawerStyle === 'cascade'"
            :disabled="isDataViewLoading"
            @click="handleApplyClick"
          >
            {{ t('ll.apply') }}
          </Button>
        </div>
      </template>
    </Modal>
  </Box>
</template>
